import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BarChart3, RefreshCw, Share2, MapPin, ExternalLink, Trophy, Tag } from "lucide-react";
import { toast } from "sonner";
import { NET_META, SocialPublishBadge } from "@/components/SocialPublishBadge";

const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—"; } catch { return "—"; } };

export default function AdminSocialReach() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/social/leaderboard"); setData(data); }
    catch { toast.error("Erreur de chargement."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const nets = Object.keys(NET_META);
  const maxCity = data?.by_city?.[0]?.total || 1;

  return (
    <div data-testid="admin-social-reach">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="admin-social-reach-title">
            <BarChart3 className="text-[#E05638]" size={26} /> Portée réseaux
          </h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1">Les offres les plus partagées sur les réseaux, par réseau et par ville — pour repérer ce qui plaît et concentrer vos efforts.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-social-reach-reload">
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm mt-6">Chargement…</div>
      ) : !data || data.total_shares === 0 ? (
        <div className="mt-6 text-slate-500 text-sm bg-white border border-dashed rounded-2xl p-8 text-center" data-testid="social-reach-empty">
          Aucun partage enregistré pour le moment. Partagez une offre depuis « Publications des offres » : elle apparaîtra ici.
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4 col-span-2 lg:col-span-1" data-testid="reach-total">
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-1.5"><Share2 size={18} className="text-[#E05638]"/> {data.total_shares}</div>
              <div className="text-xs text-slate-500">Partages au total</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="reach-offers">
              <div className="text-2xl font-bold text-slate-900">{data.offers_shared}</div>
              <div className="text-xs text-slate-500">Offres partagées</div>
            </div>
            {nets.map((n) => (
              <div key={n} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`reach-net-${n}`}>
                <div className="flex items-center gap-1.5">
                  <span className={`w-2.5 h-2.5 rounded-full ${NET_META[n].cls}`} />
                  <span className="text-2xl font-bold text-slate-900">{data.by_network?.[n] || 0}</span>
                </div>
                <div className="text-xs text-slate-500">{NET_META[n].label}</div>
              </div>
            ))}
          </div>

          <div className="mt-5 grid lg:grid-cols-3 gap-4">
            {/* Top villes */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="reach-cities">
              <div className="font-semibold text-slate-800 text-sm flex items-center gap-1.5 mb-3"><MapPin size={16} className="text-[#E05638]"/> Villes les plus actives</div>
              {(data.by_city || []).length === 0 ? <p className="text-xs text-slate-400">—</p> : (
                <ul className="space-y-2">
                  {data.by_city.map((c) => (
                    <li key={c.city} data-testid={`reach-city-${c.city}`}>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="font-medium text-slate-700 truncate">{c.city}</span>
                        <span className="text-slate-500 font-semibold">{c.total}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-[#E05638]" style={{ width: `${Math.round((c.total / maxCity) * 100)}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Classement des offres */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 lg:col-span-2" data-testid="reach-top-offers">
              <div className="font-semibold text-slate-800 text-sm flex items-center gap-1.5 mb-3"><Trophy size={16} className="text-[#E05638]"/> Offres les plus partagées</div>
              <div className="space-y-2">
                {data.top.map((o, i) => (
                  <div key={o.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50" data-testid={`reach-offer-${o.id}`}>
                    <div className="w-6 text-center font-bold text-slate-400 shrink-0">{i + 1}</div>
                    {o.image
                      ? <img src={o.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-100 shrink-0" />
                      : <div className="w-12 h-12 rounded-lg bg-slate-100 grid place-items-center text-slate-300 shrink-0"><Tag size={18} /></div>}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 text-sm truncate">{o.title}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                        {o.business && <span className="truncate">{o.business}</span>}
                        {o.city && <span className="inline-flex items-center gap-0.5"><MapPin size={11}/> {o.city}</span>}
                        <span className="text-slate-400">· dernier {fmt(o.last)}</span>
                      </div>
                      <div className="mt-1"><SocialPublishBadge socialPosts={o.counts} socialCounts={o.counts} testid={`reach-badge-${o.id}`} /></div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-bold text-[#E05638]">{o.total}</div>
                      <a href={`/offre/${o.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-[#E05638]"><ExternalLink size={11}/> Voir</a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
